import dayjs from 'dayjs'
import Head from 'next/head'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next/pages'
import styles from './EncyclopediaArticle.module.scss'
import { ArticleBody, ArticleFooter, ArticleHeader, ArticleIntro, ArticleValve } from 'components/article'
import ContactCta from 'components/contact-cta/ContactCta'
import { generateArticleStructuredData } from 'utils/structuredData'
import ContentBlocks from 'components/article/content-blocks/ContentBlocks'
import { EncyclopediaArticleProps } from 'features/encyclopedia/article/types'
import { getBoltWebsiteOrigin } from 'utils/helpers'
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs'
import ArticleGrid from 'components/article/article-grid/ArticleGrid'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import { Language } from 'types/language'

const EncyclopediaArticle = ({ article, allArticles }: EncyclopediaArticleProps) => {
  // i18n
  const { t } = useTranslation('encyclopedia')

  return (
    <>
      {/* SEO */}
      <Head>
        <script
          type="application/ld+json"
          key="article-jsonld"
          dangerouslySetInnerHTML={generateArticleStructuredData({
            ...article,
            authorName: article.author?.firstName,
            datePublished: article.publishedAt,
            dateUpdated: article.updatedAt,
            thumbnail: article.thumbnail.url,
            type: 'Article'
          })}
        />
        <link rel="canonical" href={`${getBoltWebsiteOrigin()}/nl/encyclopedie/${article.category.slug}/${article.slug}`} />
      </Head>

      <div className={styles['article-container']}></div>

      {/* CONTENT */}
      <section className={styles.article}>
        <Breadcrumbs className={styles.breadcrumbs} />

        {/* HEADER */}
        <ArticleHeader
          author={{
            date: dayjs(article.publishedAt).format('DD/MM/YYYY'),
            imagePublicId: article.author?.avatarPublicId,
            name: article.author?.firstName
          }}
          tag={article.category.title}
          title={article.title}
        />

        {/* INTRO */}
        <ArticleIntro>{parse(article.description ?? '')}</ArticleIntro>

        {/* BODY */}
        <ArticleBody>
          <ContentBlocks contentBlocks={article.contentBlocks ?? []} />
        </ArticleBody>

        {/* VALVE (afsluiter) */}
        <ArticleValve>
          <p>
            {t(
              'articleValve',
              'Bolt helpt jou wegwijs maken in energie. Inzicht in je energie-aansluiting en energie algemeen helpen jou om de juiste keuzes te maken richting een klimaatneutrale toekomst.'
            )}
          </p>
        </ArticleValve>

        {/* FOOTER */}
        <ArticleFooter />

        {/* AUTHOR */}
        {article?.author && (
          <ContactCta
            avatarPublicId={article.author.avatarPublicId}
            description={(article.author?.personalizedFooter?.description as string) ?? t('articleFooterDescription', 'Lorem ipsum')}
            title={
              article.author?.personalizedFooter?.title ??
              t('articleFooterGreeting', 'Hallo, ik ben {{ name }}', { name: article.author.firstName })
            }
          />
        )}
      </section>

      {/* MORE ARTICLES */}
      <ArticleGrid
        title={t('more.title')}
        description={t('more.description')}
        action={
          <Link representation="button" variant="secondary" href={routes(Language.DUTCH).encyclopedia}>
            {t('more.action')}
          </Link>
        }
        articles={allArticles}
        className={styles['more-articles']}
      />
    </>
  )
}

export default EncyclopediaArticle
